/**
 * `FIELD("NAME")` for the SDMS Template Builder's design sheet.
 *
 * In a template, `FIELD("NAME")` is a DevExpress mail-merge placeholder; the merge
 * replaces it with the sample data. Univer has no such function, so without this
 * every merge cell renders `#NAME?`. In the DESIGNER it evaluates to `[NAME]` — the
 * same text DevExpress caches in the file — while the cell keeps its real formula,
 * so the designer sees the token and edits the formula.
 *
 * Registered on the engine's IFunctionService right AFTER createWorkbook (see
 * features/analysis/limsRoundFn.ts for why the facade path and an earlier
 * registration do not work).
 */
import { BaseFunction, IFunctionService, StringValueObject, type FUNCTION_NAMES_MATH } from '@univerjs/engine-formula';
import type { BaseValueObject } from '@univerjs/engine-formula';

class FieldFunction extends BaseFunction {
  override calculate(...args: BaseValueObject[]) {
    const a = args[0] as unknown as { getValue?: () => unknown } | undefined;
    const name = String(a?.getValue?.() ?? '').trim().toUpperCase();
    return StringValueObject.create(name ? `[${name}]` : '');
  }
}

/** Registers FIELD on a Univer instance; logs (never throws) when the engine is unavailable. */
export function registerFieldFunction(univer: unknown): void {
  try {
    const injector = (univer as { __getInjector?: () => { get: (id: unknown) => unknown } })?.__getInjector?.();
    const fnService = injector?.get(IFunctionService) as { registerExecutors?: (...f: BaseFunction[]) => void } | undefined;
    if (!fnService?.registerExecutors) {
      // eslint-disable-next-line no-console
      console.error('[SDMS Template Builder] IFunctionService unavailable — FIELD() cells will show #NAME?.');
      return;
    }
    fnService.registerExecutors(new FieldFunction('FIELD' as unknown as typeof FUNCTION_NAMES_MATH.ABS));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[SDMS Template Builder] FIELD() registration failed.', e);
  }
}
